import { PreviewState } from '../ecardsPreact.constants/previewState';

const ECARD_DESIGN_NAME = 'ecard-design';
const PREVIEW_STATE_ATTR = 'preview-state';

export default class EcardDesign extends HTMLElement {
    constructor() {
        super();

        this._handlePreviewStateChange = this._handlePreviewStateChange.bind(this);
        this.handlePrevClick = this.handlePrevClick.bind(this);
        this.handleNextClick = this.handleNextClick.bind(this);
        this.handlePlayClick = this.handlePlayClick.bind(this);
        this.handlePauseClick = this.handlePauseClick.bind(this);
        this.handleReplayClick = this.handleReplayClick.bind(this);
        this.handleVideoPlay = this.handleVideoPlay.bind(this);
        this.handleVideoPause = this.handleVideoPause.bind(this);

        this.setAttribute(PREVIEW_STATE_ATTR, PreviewState.Front);

        this.isGtmCard = this.classList.contains('ecard-preview--gtm');

        this._pips = Object.values(PreviewState)
            .map((state) => {
                if (!this.isGtmCard && state === PreviewState.Gtm) {
                    return null;
                }

                const pip = document.createElement('span');

                pip.className = `preview-controls__pip${state === PreviewState.Front ? ' preview-controls__pip--active' : ''}`;
                pip.setAttribute('key', state);
                pip.addEventListener('click', () => {
                    this.setAttribute(PREVIEW_STATE_ATTR, state);
                });
                return pip;
            })
            .filter((pip) => pip !== null);
    }

    connectedCallback() {
        this._containerEl = this.querySelector(`[${ECARD_DESIGN_NAME}-container]`);
        this._pipsEl = this.querySelector(`[${ECARD_DESIGN_NAME}-pips]`);
        this._prevEl = this.querySelector(`[${ECARD_DESIGN_NAME}-prev]`);
        this._nextEl = this.querySelector(`[${ECARD_DESIGN_NAME}-next]`);
        this._playEl = this.querySelector(`[${ECARD_DESIGN_NAME}-play]`);
        this._pauseEl = this.querySelector(`[${ECARD_DESIGN_NAME}-pause]`);
        this._replayEl = this.querySelector(`[${ECARD_DESIGN_NAME}-replay]`);
        this._videoEl = this.querySelector(`[${ECARD_DESIGN_NAME}-video]`);
        this._videoControlsEl = this.querySelector(`[${ECARD_DESIGN_NAME}-video-controls]`);
        this._envelopeEl = this.closest('ecard-envelope');

        if (this._containerEl !== null && this._pipsEl !== null && this._prevEl !== null && this._nextEl !== null) {
            this._pips.forEach((pip) => {
                this._pipsEl.append(pip);
            });
            this._prevEl.addEventListener('click', this.handlePrevClick);
            this._nextEl.addEventListener('click', this.handleNextClick);

            if (this._videoEl !== null && this._playEl !== null && this._pauseEl !== null) {
                this._playEl.addEventListener('click', this.handlePlayClick);
                this._pauseEl.addEventListener('click', this.handlePauseClick);
                this._videoEl.addEventListener('play', this.handleVideoPlay);
                this._videoEl.addEventListener('pause', this.handleVideoPause);
            }

            if (this._envelopeEl !== null && this._replayEl !== null) {
                this._replayEl.disabled = false;
                this._replayEl.addEventListener('click', this.handleReplayClick);
            } else if (this._videoEl !== null && this._playEl) {
                this._playEl.disabled = false;
            }

            this._observer = new MutationObserver(this._handlePreviewStateChange);
            this._observer.observe(this, { attributes: true, childList: false, subtree: false });
        } else {
            console.warn(`<${ECARD_DESIGN_NAME}> custom element has missing data attributes`);
        }
    }

    disconnectedCallback() {
        if (this._observer !== undefined) {
            this._observer.disconnect();
        }
    }

    _handlePreviewStateChange(mutationsList) {
        for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' && mutation.attributeName === PREVIEW_STATE_ATTR) {
                const newState = this.getAttribute(PREVIEW_STATE_ATTR);
                switch (newState) {
                    case PreviewState.Back:
                        this._containerEl.classList.add('ecard-preview__container--back');
                        this._containerEl.classList.remove('ecard-preview__container--gtm', 'ecard-preview__container--front');
                        this._prevEl.disabled = false;
                        this._nextEl.disabled = true;
                        //this._videoControlsEl?.classList.add('preview-controls__video--hidden');
                        break;
                    case PreviewState.Gtm:
                        this._containerEl.classList.add('ecard-preview__container--gtm');
                        this._containerEl.classList.remove('ecard-preview__container--front', 'ecard-preview__container--back');
                        this._prevEl.disabled = false;
                        this._nextEl.disabled = false;
                        //this._videoControlsEl?.classList.add('preview-controls__video--hidden');
                        break;
                    case PreviewState.Front:
                    default:
                        this._containerEl.classList.add('ecard-preview__container--front');
                        this._containerEl.classList.remove('ecard-preview__container--gtm', 'ecard-preview__container--back');
                        this._prevEl.disabled = true;
                        this._nextEl.disabled = false;
                        //this._videoControlsEl?.classList.remove('preview-controls__video--hidden');
                        break;
                }

                this._pips.forEach((pip) => {
                    if (pip.getAttribute('key') === newState) {
                        pip.classList.add('preview-controls__pip--active');
                    } else {
                        pip.classList.remove('preview-controls__pip--active');
                    }
                });
            }
        }
    }

    handlePrevClick() {
        const currentState = this.getAttribute(PREVIEW_STATE_ATTR);
        let newState = PreviewState.Front;

        if (this.isGtmCard) {
            const optionsArr = Object.values(PreviewState);
            const currentIndex = optionsArr.indexOf(currentState);
            const newIndex = currentIndex <= 0 ? 0 : currentIndex - 1;
            newState = PreviewState[optionsArr[newIndex]];
        }

        this.setAttribute(PREVIEW_STATE_ATTR, newState);
    }

    handleNextClick() {
        const currentState = this.getAttribute(PREVIEW_STATE_ATTR);
        let newState = PreviewState.Back;

        if (this.isGtmCard) {
            const optionsArr = Object.values(PreviewState);
            const currentIndex = optionsArr.indexOf(currentState);
            const newIndex = currentIndex >= optionsArr.length - 1 ? optionsArr.length - 1 : currentIndex + 1;
            newState = PreviewState[optionsArr[newIndex]];
        }

        this.setAttribute(PREVIEW_STATE_ATTR, newState);
    }

    handlePlayClick() {
        this._videoEl.play();
        this._videoEl.muted = false;
    }

    handlePauseClick() {
        this._videoEl.pause();
    }

    handleReplayClick() {
        this._replayEl.disabled = true;
        this.setAttribute(PREVIEW_STATE_ATTR, PreviewState.Front);
        this._envelopeEl.close(false, true).then(() => {
            this._replayEl.disabled = false;
            this._envelopeEl.open();
        });
    }

    handleVideoPlay() {
        this._playEl.disabled = true;
        this._pauseEl.disabled = false;
        this._replayEl.disabled = true;
    }

    handleVideoPause() {
        this._pauseEl.disabled = true;

        if ((this._envelopeEl !== null && this._videoEl.currentTime === this._videoEl.duration) || this._videoEl.currentTime === 0) {
            this._replayEl.disabled = false;
        } else {
            this._playEl.disabled = false;
        }
    }
}

customElements.define(ECARD_DESIGN_NAME, EcardDesign);
